import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Comment, Reply } from '../types';

interface CommentStore {
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  fetchComments: (projectId: string, pageUrl?: string) => Promise<void>;
  addComment: (guestPassword: string, comment: Omit<Comment, 'id' | 'status' | 'replies' | 'isRead' | 'number'>, screenshotBlob?: Blob) => Promise<void>;
  addReply: (commentId: string, reply: Omit<Reply, 'id'>) => Promise<void>;
  resolveComment: (commentId: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  markAsRead: (commentId: string) => Promise<void>;
  getCommentsForCurrentPage: () => Comment[];
}

export const useCommentStore = create<CommentStore>((set, get) => ({
  comments: [],
  isLoading: false,
  error: null,

  fetchComments: async (projectId: string, pageUrl?: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('guest_password', projectId)
        .maybeSingle();

      if (projectError) throw projectError;
      if (!projectData) throw new Error('Project not found');

      // Build query - if pageUrl provided, filter by it
      let query = supabase
        .from('comments')
        .select('*, replies(*)')
        .eq('project_id', projectData.id);

      // For now, we'll fetch all comments and filter client-side
      // In production, you might want to add pageUrl as a column for better performance
      const { data: commentsData, error: commentsError } = await query
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      // Filter by page URL if provided
      let filteredComments = commentsData || [];
      if (pageUrl) {
        filteredComments = filteredComments.filter(comment => {
          try {
            const commentPosition = typeof comment.position === 'string' 
              ? JSON.parse(comment.position) 
              : comment.position;
            return commentPosition?.pageUrl === pageUrl;
          } catch {
            return false;
          }
        });
      }

      set({ comments: filteredComments, isLoading: false });
    } catch (error) {
      console.error('Error fetching comments:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch comments',
        isLoading: false 
      });
    }
  },

  getCommentsForCurrentPage: () => {
    const currentPageUrl = window.location.href;
    const { comments } = get();
    
    return comments.filter(comment => {
      try {
        const position = typeof comment.position === 'string' 
          ? JSON.parse(comment.position) 
          : comment.position;
        return position?.pageUrl === currentPageUrl;
      } catch {
        return false;
      }
    });
  },

  addComment: async (guestPassword: string, comment, screenshotBlob) => {
    try {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('guest_password', guestPassword)
        .single();

      if (projectError) throw projectError;

      const { data: lastComment } = await supabase
        .from('comments')
        .select('number')
        .eq('project_id', projectData.id)
        .order('number', { ascending: false })
        .limit(1)
        .single();

      const newNumber = (lastComment?.number || 0) + 1;

      // Handle screenshot upload if provided
      let screenshotUrl = null;
      if (screenshotBlob) {
        try {
          console.log('📸 Uploading user-selected screenshot...');
          
          const filename = `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.jpg`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('project-screenshots')
            .upload(filename, screenshotBlob);
            
          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from('project-screenshots')
              .getPublicUrl(filename);
            screenshotUrl = publicUrl;
            console.log('✅ User screenshot uploaded:', screenshotUrl);
          } else {
            console.warn('Failed to upload screenshot:', uploadError);
          }
        } catch (screenshotError) {
          console.warn('Failed to upload screenshot:', screenshotError);
          // Don't fail the comment creation if screenshot fails
        }
      }

      const { data, error } = await supabase
        .from('comments')
        .insert([{
          ...comment,
          project_id: projectData.id,
          status: 'active',
          number: newNumber,
          is_read: false,
          screenshot_url: screenshotUrl,
        }])
        .select()
        .single();

      if (error) throw error;
      
      set(state => ({
        comments: [...state.comments, { ...data, replies: [], isRead: false }]
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  addReply: async (commentId: string, reply) => {
    try {
      const { data, error } = await supabase
        .from('replies')
        .insert([{ ...reply, comment_id: commentId }])
        .select()
        .single();

      if (error) throw error;

      // Update the comment's read status when a reply is added
      await supabase
        .from('comments')
        .update({ is_read: true })
        .eq('id', commentId);

      set(state => ({
        comments: state.comments.map(comment =>
          comment.id === commentId
            ? { ...comment, replies: [...comment.replies, data], isRead: true }
            : comment
        )
      }));
    } catch (error) {
      console.error('Error adding reply:', error);
      throw error;
    }
  },

  resolveComment: async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .update({ status: 'resolved', is_read: true })
        .eq('id', commentId);

      if (error) throw error;

      set(state => ({
        comments: state.comments.map(comment =>
          comment.id === commentId
            ? { ...comment, status: 'resolved', isRead: true }
            : comment
        )
      }));
    } catch (error) {
      console.error('Error resolving comment:', error);
      throw error;
    }
  },

  deleteComment: async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      set(state => ({
        comments: state.comments.filter(comment => comment.id !== commentId)
      }));
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },

  markAsRead: async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .update({ is_read: true })
        .eq('id', commentId);

      if (error) throw error;

      set(state => ({
        comments: state.comments.map(comment =>
          comment.id === commentId
            ? { ...comment, isRead: true }
            : comment
        )
      }));
    } catch (error) {
      console.error('Error marking comment as read:', error);
      throw error;
    }
  }
}));